import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { recordPageVisit } from "@/lib/api";
import api from "@/lib/api";
import QuickEligibilityWidget from "@/components/QuickEligibilityWidget";
import VideoShowcase from "@/components/VideoShowcase";
import { 
  MessageCircle, 
  Video, 
  Stethoscope, 
  BookMarked,
  Heart,
  Phone,
  ArrowRight,
  Shield,
  Clock,
  HandHeart,
  ClipboardList,
  MessageSquare,
  Building2,
  Award,
  Users,
  TrendingUp,
  Star,
  ExternalLink,
  ThumbsUp,
  HeartHandshake,
  BookOpen,
  Play
} from "lucide-react";

// Features use translation keys
const getFeatures = (t) => [
  {
    icon: MessageCircle,
    title: t("landing.features.aiSupport.title", "AI-Powered Support"),
    description: t("landing.features.aiSupport.description", "Get answers to your hospice questions with our compassionate AI assistant. Disease-specific guidance available."),
    link: "/chat",
    color: "bg-primary/10 text-primary"
  },
  {
    icon: Video,
    title: t("landing.features.videos.title", "Training Videos"),
    description: t("landing.features.videos.description", "Watch expert-led videos on wound care, turning & positioning, medication administration, and more."),
    link: "/videos",
    color: "bg-teal-100 text-teal-700"
  },
  {
    icon: Stethoscope,
    title: t("landing.features.symptoms.title", "Symptom Management"),
    description: t("landing.features.symptoms.description", "Comprehensive guides on managing symptoms with clear 'when to call' alerts. Export as PDF."),
    link: "/symptoms",
    color: "bg-secondary text-secondary-foreground"
  },
  {
    icon: Heart,
    title: t("landing.features.programs.title", "Complementary Programs"),
    description: t("landing.features.programs.description", "Massage therapy, end-of-life doula services, and aftercare support - beyond traditional hospice care."),
    link: "/programs",
    color: "bg-pink-100 text-pink-700"
  },
  {
    icon: BookMarked,
    title: t("landing.features.journal.title", "Caregiver Journal"),
    description: t("landing.features.journal.description", "Track daily observations, mood, symptoms, and medications. Share insights with your care team."),
    link: "/journal",
    color: "bg-teal-100 text-teal-700"
  },
  {
    icon: Phone,
    title: t("landing.features.resources.title", "Community Resources"),
    description: t("landing.features.resources.description", "Find local support services in Northern Virginia - transportation, meals, counseling, and more with click-to-call."),
    link: "/resources",
    color: "bg-secondary text-secondary-foreground"
  }
];

const getStats = (t) => [
  { number: "24/7", label: t("landing.stats.support", "AI Support") },
  { number: "10+", label: t("landing.stats.resources", "Local Resources") },
  { number: "100%", label: t("landing.stats.compassionate", "Compassionate") },
];

// Default CAHPS data for fallback
const DEFAULT_CAHPS = {
  star_rating: 4,
  measures: [
    { id: "communication", name: "Communication with Family", score: 82, description: "How well staff communicate" },
    { id: "timely_help", name: "Getting Timely Help", score: 78, description: "Response to urgent needs" },
    { id: "respect", name: "Treating Patient with Respect", score: 88, description: "Dignity and respect shown" },
    { id: "emotional", name: "Emotional & Spiritual Support", score: 85, description: "Support for emotional needs" },
    { id: "pain", name: "Help for Pain & Symptoms", score: 80, description: "Managing pain and discomfort" },
    { id: "training", name: "Training Family to Care", score: 76, description: "Teaching caregiving skills" },
    { id: "rating", name: "Rating of Hospice", score: 84, description: "Overall rating (0-10 scale)" },
    { id: "recommend", name: "Would Recommend", score: 86, description: "Would recommend to others" }
  ]
};

// Map measure IDs to icons
const MEASURE_ICONS = {
  communication: MessageSquare,
  timely_help: Clock,
  respect: HeartHandshake,
  emotional: Heart,
  pain: Stethoscope,
  training: Users,
  rating: Star,
  recommend: ThumbsUp
};

const MEASURE_COLORS = {
  communication: "text-blue-600 bg-blue-100",
  timely_help: "text-green-600 bg-green-100",
  respect: "text-purple-600 bg-purple-100",
  emotional: "text-pink-600 bg-pink-100",
  pain: "text-red-600 bg-red-100",
  training: "text-teal-600 bg-teal-100",
  rating: "text-yellow-600 bg-yellow-100",
  recommend: "text-indigo-600 bg-indigo-100"
};

export default function Landing() {
  const [cahpsData, setCahpsData] = useState(DEFAULT_CAHPS);
  const { t } = useTranslation();
  
  // Get translated features and stats
  const features = getFeatures(t);
  const stats = getStats(t);

  useEffect(() => {
    recordPageVisit("home").catch(console.error);
    
    // Fetch CAHPS scores from API
    api.get("/cahps-scores")
      .then(res => setCahpsData(res.data))
      .catch(() => console.log("Using default CAHPS data"));
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/80 via-white to-primary/5" />
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8 animate-fade-in">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium border border-primary/20 backdrop-blur-sm">
                <Heart className="w-4 h-4" />
                <span>{t("landing.hero.badge", "Serving Northern Virginia")}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              </div>
              
              {/* Main Heading - Clear Mission Statement */}
              <div className="space-y-3">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight">
                  {t("app.name")}
                </h1>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-medium text-muted-foreground">
                  {t("landing.hero.subtitle", "Compassionate Hospice Care for You and Your Family")}
                </h2>
              </div>
              
              {/* Value Proposition - Clear & Actionable */}
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                {t("landing.hero.description", "Expert care when it matters most. Our dedicated team provides comfort, dignity, and support — with 24/7 access to nurses, AI-powered guidance, and a family portal to keep everyone connected.")}
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">{t("landing.stats.support247", "24/7 Nurse Hotline")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">{t("landing.stats.team", "Interdisciplinary Team")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">{t("landing.stats.rating", "")}</span>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4">
                <Link to="/chat" data-testid="hero-cta-chat">
                  <Button className="rounded-full px-8 py-6 text-lg bg-primary hover:bg-primary/90 shadow-soft transition-all hover:scale-105 hover:shadow-lg">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t("landing.hero.cta.chat", "Talk to Our AI Guide")}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/demo" data-testid="hero-cta-demo">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-primary/20 hover:bg-primary/5 hover:border-primary/40 transition-all">
                    <Video className="w-5 h-5 mr-2" />
                    {t("landing.hero.cta.demo", "Watch Our Story")}
                  </Button>
                </Link>
              </div>
              
              {/* Contact Quick Access */}
              <div className="flex items-center gap-4 pt-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-primary" />
                <span>{t("landing.hero.callUs", "Call us anytime:")}</span>
                <a href="tel:7035787108" className="font-medium text-primary hover:underline">703-578-7108</a>
              </div>
            </div>
            <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="relative rounded-3xl overflow-hidden shadow-hover">
                <img 
                  src="https://images.unsplash.com/photo-1603839834958-ae04ebbb3ec1?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt={t("landing.hero.imageAlt", "Compassionate caregiver support")}
                  className="w-full h-[400px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 shadow-hover animate-fade-in" style={{ animationDelay: "0.4s" }}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t("landing.hero.alwaysHere", "Always Here")}</p>
                    <p className="text-sm text-muted-foreground">{t("landing.hero.support247", "24/7 Support Available")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="container-max px-6 md:px-12">
          <div className="text-center space-y-4 mb-10">
            <p className="caption text-primary flex items-center justify-center gap-2">
              <Play className="w-4 h-4" />
              See It In Action
            </p>
            <h2 className="heading-2 text-foreground">Experience Compassionate Care</h2>
            <p className="body-base max-w-2xl mx-auto text-muted-foreground">
              Watch how Goodwin Cares brings families, patients, and caregivers together 
              through technology that truly understands the hospice journey.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <VideoShowcase 
              src="/videos/goodwin-cares-promo.mp4"
              title="Goodwin Cares"
              subtitle="Compassionate Care, Powered by Technology"
            />
          </div>
          
          <div className="flex justify-center mt-8">
            <Link to="/demo">
              <Button variant="outline" className="rounded-full">
                <ArrowRight className="w-4 h-4 mr-2" />
                Take the Interactive Tour
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-border/50">
        <div className="container-max px-6 md:px-12">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-serif font-semibold text-primary">{stat.number}</p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-muted/30" data-testid="features-section">
        <div className="container-max">
          <div className="text-center space-y-4 mb-16">
            <p className="caption text-primary">How We Help</p>
            <h2 className="heading-2 text-foreground">Everything You Need, In One Place</h2>
            <p className="body-base max-w-2xl mx-auto">
              From disease-specific guidance to real-time communication with your care team, 
              we're here to make the hospice journey less overwhelming.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Link 
                  key={index} 
                  to={feature.link}
                  data-testid={`feature-card-${index}`}
                >
                  <Card className="glass-card h-full group cursor-pointer card-hover border-0">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-2xl ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-serif font-medium text-foreground mb-2 group-hover:text-primary transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding" data-testid="why-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Doctor consulting with patient"
                className="rounded-3xl shadow-hover w-full h-[450px] object-cover"
              />
            </div>
            <div className="space-y-8">
              <div>
                <p className="caption text-primary mb-4">Why Goodwin Cares</p>
                <h2 className="heading-2 text-foreground">
                  Compassion Meets Technology
                </h2>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Available Anytime</h4>
                    <p className="text-muted-foreground">Get answers and support 24/7, whenever you need guidance.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <HandHeart className="w-6 h-6 text-teal-700" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Disease-Specific Support</h4>
                    <p className="text-muted-foreground">Get guidance tailored to Cancer, Heart Failure, Dementia, COPD, Kidney Failure, or ALS.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-1">Connected to Your Team</h4>
                    <p className="text-muted-foreground">Message your hospice office directly and share journal insights with providers.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facility Partner Section */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="facility-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-teal-400">
                <Building2 className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">Healthcare Partners</span>
              </div>
              <h2 className="heading-2 text-white">
                Facility Partner Portal
              </h2>
              <p className="body-lg text-white/70">
                Join Goodwin Hospice as a referral partner. Submit secure patient referrals, 
                access our AI-powered hospice eligibility assessment tool, and connect directly 
                with our care team.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-white/80">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-teal-400" />
                  </div>
                  Secure HIPAA-compliant patient referrals
                </li>
                <li className="flex items-center gap-3 text-white/80">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-teal-400" />
                  </div>
                  AI-powered hospice criteria questionnaire
                </li>
                <li className="flex items-center gap-3 text-white/80">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-teal-400" />
                  </div>
                  Real-time referral status tracking
                </li>
                <li className="flex items-center gap-3 text-white/80">
                  <div className="w-6 h-6 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <ArrowRight className="w-4 h-4 text-teal-400" />
                  </div>
                  24/7 referral support line
                </li>
              </ul>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/facility" data-testid="facility-portal-btn">
                  <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8 py-6 text-lg font-medium">
                    <Building2 className="w-5 h-5 mr-2" />
                    Access Partner Portal
                  </Button>
                </Link>
                <Link to="/facility/criteria" data-testid="criteria-tool-btn">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium">
                    <ClipboardList className="w-5 h-5 mr-2" />
                    Try Criteria Tool
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-500/20 to-primary/20 rounded-3xl p-8 backdrop-blur">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-teal-400">500+</p>
                    <p className="text-sm text-white/70">Partner Facilities</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-teal-400">24/7</p>
                    <p className="text-sm text-white/70">Referral Support</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-teal-400">&lt;2hr</p>
                    <p className="text-sm text-white/70">Response Time</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-teal-400">AI</p>
                    <p className="text-sm text-white/70">Eligibility Tool</p>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-white/80 text-center">
                    "The AI criteria tool saves us hours of eligibility assessment. 
                    Referrals are processed faster than ever."
                  </p>
                  <p className="text-xs text-white/50 text-center mt-2">
                    — Case Manager, Fairfax Hospital
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Physician Champion Section */}
      <section className="section-padding bg-gradient-to-br from-amber-50 to-yellow-50" data-testid="physician-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <Award className="w-8 h-8 text-yellow-500" />
                  <h3 className="font-bold text-xl">Hospice Champion Tiers</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-amber-50">
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                      <Award className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-medium">Bronze</p>
                      <p className="text-sm text-muted-foreground">3+ referrals</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-100">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <Award className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium">Silver</p>
                      <p className="text-sm text-muted-foreground">10+ referrals, 5+ early</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-yellow-100">
                    <div className="w-10 h-10 rounded-full bg-yellow-300 flex items-center justify-center">
                      <Award className="w-5 h-5 text-yellow-700" />
                    </div>
                    <div>
                      <p className="font-medium">Gold</p>
                      <p className="text-sm text-muted-foreground">25+ referrals, 10+ early</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 rounded-lg bg-purple-100">
                    <div className="w-10 h-10 rounded-full bg-purple-300 flex items-center justify-center">
                      <Award className="w-5 h-5 text-purple-700" />
                    </div>
                    <div>
                      <p className="font-medium">Platinum</p>
                      <p className="text-sm text-muted-foreground">50+ referrals, 25+ early</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="flex items-center gap-2 text-yellow-600">
                <Stethoscope className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">For Physicians</span>
              </div>
              <h2 className="heading-2 text-foreground">
                Become a Hospice Champion
              </h2>
              <p className="body-lg text-muted-foreground">
                Join our physician network dedicated to early hospice referrals and better end-of-life care. 
                Access AI-powered patient insights, track your panel, and earn recognition for making a difference.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span>AI predictive insights for your patient panel</span>
                </li>
                <li className="flex items-center gap-3">
                  <ClipboardList className="w-5 h-5 text-blue-500" />
                  <span>Goals of care documentation tools</span>
                </li>
                <li className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-purple-500" />
                  <span>One-click hospice referrals</span>
                </li>
                <li className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-yellow-500" />
                  <span>CME credits and recognition tiers</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/physician" data-testid="physician-portal-btn">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-full px-8 py-6 text-lg font-medium">
                    <Stethoscope className="w-5 h-5 mr-2" />
                    Join Champion Program
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAHPS Quality Scores Section */}
      <section className="section-padding bg-white" data-testid="cahps-section">
        <div className="container-max">
          <div className="text-center space-y-4 mb-12">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Star className="w-5 h-5 fill-primary" />
              <span className="text-sm font-medium uppercase tracking-wider">Medicare Quality Ratings</span>
            </div>
            <h2 className="heading-2 text-foreground">
              Our CAHPS Hospice Survey Scores
            </h2>
            <p className="body-base max-w-2xl mx-auto">
              We're proud of our quality ratings. These scores come from real feedback from families we've served,
              as reported to Medicare through the CAHPS Hospice Survey.
            </p>
          </div>

          {/* Star Rating Summary */}
          <div className="flex flex-col items-center mb-12">
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-8 shadow-soft border border-yellow-100 max-w-lg w-full text-center">
              {cahpsData.facility && (
                <p className="text-sm font-medium text-foreground mb-1">{cahpsData.facility.facility_name}</p>
              )}
              <p className="text-xs text-muted-foreground mb-3">
                {cahpsData.facility?.ccn ? `CCN: ${cahpsData.facility.ccn}` : "Family Caregiver Survey Rating"}
              </p>
              <div className="flex items-center justify-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className={`w-8 h-8 ${star <= (cahpsData.star_rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'fill-yellow-200 text-yellow-200'}`} />
                ))}
              </div>
              <p className="text-3xl font-bold text-foreground">{cahpsData.star_rating || "N/A"} out of 5 Stars</p>
              <p className="text-sm text-muted-foreground mt-2">Based on Medicare Care Compare</p>
            </div>
          </div>

          {/* Individual CAHPS Measures */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cahpsData.measures.map((measure, index) => {
              const Icon = MEASURE_ICONS[measure.id] || Star;
              const color = MEASURE_COLORS[measure.id] || "text-gray-600 bg-gray-100";
              return (
                <Card key={index} className="glass-card border-0 hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-4`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h4 className="font-medium text-foreground mb-1">{measure.name}</h4>
                    <p className="text-xs text-muted-foreground mb-3">{measure.description}</p>
                    <div className="flex items-end gap-2">
                      <span className="text-3xl font-bold text-primary">{measure.score}%</span>
                      <span className="text-xs text-muted-foreground pb-1">top box</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-1000"
                        style={{ width: `${measure.score}%` }}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Source Link */}
          <div className="mt-10 text-center">
            <a 
              href={cahpsData.source_url || "https://www.medicare.gov/care-compare/?guidedSearch=Hospice"} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View official scores on Medicare Care Compare
            </a>
            <p className="text-xs text-muted-foreground mt-2">
              {cahpsData.reporting_period ? `Reporting Period: ${cahpsData.reporting_period}` : ""} 
              {cahpsData.source ? ` • Data from ${cahpsData.source}` : " Data refreshed quarterly from CMS CAHPS Hospice Survey."}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Eligibility Check Section */}
      <section className="section-padding" data-testid="eligibility-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-2 text-primary">
                <ClipboardList className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-wider">Free Assessment</span>
              </div>
              <h2 className="heading-2 text-foreground">
                Is Hospice Right for Your Patient?
              </h2>
              <p className="body-lg text-muted-foreground">
                Use our quick 5-question screening tool to assess hospice eligibility. 
                No registration required. Get instant insights and next steps.
              </p>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>Takes only 2 minutes</span>
                </li>
                <li className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-primary" />
                  <span>HIPAA compliant, no data stored</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-primary" />
                  <span>Instant results with next steps</span>
                </li>
              </ul>
            </div>
            <div>
              <QuickEligibilityWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Time of Death Resources - Important Section */}
      <section className="section-padding bg-slate-800 text-white" data-testid="tod-section">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center">
            <Heart className="w-10 h-10 mx-auto mb-4 text-rose-300" />
            <h2 className="heading-2 text-white mb-4">
              When the Time Comes
            </h2>
            <p className="body-lg text-slate-300 mb-6">
              Know what to do when your loved one passes. Our guide walks you through 
              every step with compassion and clarity.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/time-of-death">
                <Button className="bg-rose-600 hover:bg-rose-700 rounded-full px-6 py-5 text-lg">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Read the Guide
                </Button>
              </Link>
              <a href="tel:703-578-7108">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6 py-5 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  24/7 Support: 703-578-7108
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white" data-testid="cta-section">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-8">
            <h2 className="heading-2 text-white">Ready to Get Started?</h2>
            <p className="body-lg text-white/80">
              Create your free account to access personalized visit questions, save your conversations, 
              and message your care team directly.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/chat" data-testid="cta-chat">
                <Button variant="secondary" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium shadow-soft">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Start Chatting
                </Button>
              </Link>
              <Link to="/contact" data-testid="cta-contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium">
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
